import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { Archive, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Loader2, HardDrive } from 'lucide-react';
import { toast } from 'sonner';

export default function DriveArchiveManagement() {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [data, setData] = useState({ archives: [], pending_failures: [], recent_logs: [] });
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadArchives = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getDriveArchives({ limit: 50 });
      setData(res.data);
    } catch (e) {
      toast.error("Impossible de charger les archives");
    } finally {
      setLoading(false);
    }
  };

  const runHealth = async () => {
    setChecking(true);
    setHealth(null);
    try {
      const res = await adminApi.getDriveHealth();
      setHealth(res.data);
      if (res.data.status === 'ok') toast.success('Connexion Google Drive OK');
      else if (res.data.status === 'not_configured') toast.warning('Google Drive non configuré');
      else toast.error('Échec de connexion Google Drive');
    } catch (e) {
      setHealth({ status: 'error', message: e.response?.data?.detail || 'Erreur inconnue' });
      toast.error('Échec du test de connexion');
    } finally {
      setChecking(false);
    }
  };

  const retryAll = async () => {
    setRetrying(true);
    try {
      const res = await adminApi.retryDriveArchives();
      toast.success(`Retry: ${res.data.success} réussi(s), ${res.data.failed} échec(s)`);
      await loadArchives();
    } catch (e) {
      toast.error("Échec du retry");
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => { loadArchives(); }, []);

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await adminApi.archiveDriveTest();
      setTestResult(res.data);
      const s = res.data.status;
      if (s === 'success') toast.success(`Archivé (${res.data.action})`);
      else if (s === 'skipped') toast.info(`Ignoré : ${res.data.reason}`);
      else toast.error(`Échec : ${res.data.error || 'inconnu'}`);
      await loadArchives();
    } catch (e) {
      setTestResult({ status: 'failed', error: e.response?.data?.detail || 'Erreur' });
      toast.error("Échec du test d'archivage");
    } finally {
      setTesting(false);
    }
  };

  const cleanupTest = async () => {
    setTesting(true);
    try {
      const res = await adminApi.archiveDriveTestCleanup();
      setTestResult(null);
      toast.success(`Nettoyé${res.data.drive_file_deleted ? ' (fichier Drive supprimé)' : ''}`);
      await loadArchives();
    } catch (e) {
      toast.error("Échec du nettoyage");
    } finally {
      setTesting(false);
    }
  };

  const StatusPill = ({ status }) => {
    const map = {
      ok: { c: 'bg-green-100 text-green-800', i: CheckCircle2, t: 'Connecté' },
      not_configured: { c: 'bg-amber-100 text-amber-800', i: AlertTriangle, t: 'Non configuré' },
      error: { c: 'bg-red-100 text-red-800', i: XCircle, t: 'Erreur' },
    };
    const m = map[status] || map.error;
    const I = m.i;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${m.c}`} data-testid="drive-health-status">
        <I className="w-4 h-4" /> {m.t}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="drive-archive-management">
      <div>
        <h1 className="text-2xl font-bold text-[#2A312C] flex items-center gap-2">
          <Archive className="w-6 h-6 text-[#A5BBA8]" /> Archivage Google Drive
        </h1>
        <p className="text-[#5C6A61] mt-1 text-sm">
          Archivage automatique des factures, avoirs, exports comptables et attestations SAP dans le Drive partagé « Mitonner – Archives ».
        </p>
      </div>

      {/* Health check */}
      <div className="bg-white rounded-lg border border-[#E3DED5] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <HardDrive className="w-5 h-5 text-[#5C6A61]" />
            <span className="font-medium text-[#2A312C]">Connexion au Shared Drive</span>
            {health && <StatusPill status={health.status} />}
          </div>
          <button
            onClick={runHealth}
            disabled={checking}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="drive-health-btn"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Tester la connexion
          </button>
        </div>
        {health && (
          <div className="mt-3 text-sm text-[#5C6A61] space-y-1" data-testid="drive-health-detail">
            {health.drive_name && <p>Drive : <strong className="text-[#2A312C]">{health.drive_name}</strong></p>}
            {health.drive_id && <p>ID : <code className="bg-[#F9F6F0] px-1 rounded">{health.drive_id}</code></p>}
            {health.message && <p className="text-amber-700">{health.message}</p>}
          </div>
        )}
      </div>

      {/* Test d'archivage */}
      <div className="bg-white rounded-lg border border-[#E3DED5] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold text-[#2A312C]">Test d'archivage (document factice)</h2>
            <p className="text-sm text-[#5C6A61]">Archive une facture factice « TEST_ARCHIVE » (aucune donnée client). Relancez pour vérifier l'idempotence.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={runTest} disabled={testing} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="drive-test-archive-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
              Archiver un document test
            </button>
            <button onClick={cleanupTest} disabled={testing} className="btn-secondary disabled:opacity-50" data-testid="drive-test-cleanup-btn">
              Nettoyer le test
            </button>
          </div>
        </div>
        {testResult && (
          <div className="mt-3 text-sm p-3 rounded-lg border" data-testid="drive-test-result"
               style={{ background: testResult.status === 'success' ? '#ECFDF5' : testResult.status === 'skipped' ? '#FEF9C3' : '#FEF2F2' }}>
            {testResult.status === 'success' && <span className="text-green-800">✅ Succès — {testResult.action} · {testResult.path}</span>}
            {testResult.status === 'skipped' && <span className="text-amber-800">↔️ Ignoré (déjà archivé) — {testResult.reason}</span>}
            {testResult.status === 'failed' && <span className="text-red-800">❌ Erreur — {testResult.error}</span>}
          </div>
        )}
      </div>

      {/* Pending failures */}
      <div className="bg-white rounded-lg border border-[#E3DED5] p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-[#2A312C]">Archivages en échec ({data.pending_failures?.length || 0})</h2>
          {data.pending_failures?.length > 0 && (
            <button onClick={retryAll} disabled={retrying} className="btn-secondary flex items-center gap-2 disabled:opacity-50" data-testid="drive-retry-all-btn">
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Rejouer tout
            </button>
          )}
        </div>
        {loading ? (
          <p className="text-[#5C6A61] text-sm">Chargement…</p>
        ) : data.pending_failures?.length === 0 ? (
          <p className="text-[#5C6A61] text-sm">Aucun archivage en échec. ✅</p>
        ) : (
          <div className="space-y-2">
            {data.pending_failures.map((q) => (
              <div key={q.queue_id} className="flex items-center justify-between text-sm border border-red-100 bg-red-50 rounded p-2">
                <span><strong>{q.doc_type}</strong> · {q.doc_id} <span className="text-red-600">— {q.last_error}</span></span>
                <button onClick={() => adminApi.retryDriveArchive(q.queue_id).then(() => { toast.success('Rejoué'); loadArchives(); }).catch(() => toast.error('Échec'))}
                  className="text-[#A5BBA8] hover:underline">Rejouer</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent archives */}
      <div className="bg-white rounded-lg border border-[#E3DED5] p-5">
        <h2 className="font-semibold text-[#2A312C] mb-3">Archives récentes ({data.archives?.length || 0})</h2>
        {loading ? (
          <p className="text-[#5C6A61] text-sm">Chargement…</p>
        ) : data.archives?.length === 0 ? (
          <p className="text-[#5C6A61] text-sm">Aucun document archivé pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#5C6A61] border-b border-[#E3DED5]">
                  <th className="py-2">Type</th><th>Document</th><th>Chemin</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.archives.map((a) => (
                  <tr key={a.archive_id} className="border-b border-[#F0EBE2]">
                    <td className="py-2">{a.doc_type}</td>
                    <td>{a.filename}</td>
                    <td className="text-[#5C6A61]">{a.folder_path}</td>
                    <td>{a.status === 'success' ? <span className="text-green-700">Archivé</span> : <span className="text-red-600">{a.status}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
